/**
 * SocialDisbursementPage — citizen recipient view of NIPOST Pay social
 * disbursements (NPower, TraderMoni, NSIP, etc).
 *
 * Live (service-financial-services, citizen endpoints):
 *   - GET  /v1/disbursements/programs              — active programmes
 *   - GET  /v1/disbursements/me/recipient          — citizen's enrolment rows
 *                                                    (lazy-enrols on first call)
 *   - POST /v1/disbursements/recipients/:id/claim  — claim PENDING; auto-credits wallet
 *
 * RBAC: Disbursement/read-own + Disbursement/claim-own — granted to the
 * `customer` role by 20260509_nipost_pay_destub_perms.sql.
 */
import { type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, Sparkles, Users } from 'lucide-react';

import {
  claimDisbursement,
  listDisbursementPrograms,
  listMyRecipientRows,
  type ClaimResponse,
  type DisbursementProgram,
  type DisbursementRecipientRow,
  type ListMyRecipientResponse,
  type ListProgramsResponse,
} from '../../lib/api/disbursements';
import { useCustomerAuth } from '../../lib/auth/useCustomerAuth';
import {
  PageHeader,
  StatusBadge,
  customerWalletQueryKey,
  formatNaira,
} from './NipostPayUI';

export function SocialDisbursementPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { token, claims } = useCustomerAuth();
  const customerId = claims?.upmpUserId ?? '';

  const programs = useQuery<ListProgramsResponse, Error>({
    queryKey: ['disbursement-programs'] as const,
    queryFn: () => listDisbursementPrograms({ bearerToken: token ?? undefined }),
    enabled: token !== null,
    retry: false,
    staleTime: 5 * 60_000,
  });

  const recipients = useQuery<ListMyRecipientResponse, Error>({
    queryKey: ['disbursement-recipients-me', customerId] as const,
    queryFn: () => listMyRecipientRows({ bearerToken: token ?? undefined }),
    enabled: token !== null,
    retry: false,
    staleTime: 30_000,
  });

  const claim = useMutation<ClaimResponse, Error, string>({
    mutationFn: (id: string) => claimDisbursement(id, { bearerToken: token ?? undefined }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['disbursement-recipients-me', customerId] });
      queryClient.invalidateQueries({ queryKey: customerWalletQueryKey(customerId) });
    },
  });

  return (
    <div>
      <PageHeader
        title="Social disbursements"
        description="Government bulk-payment programmes credited to your NIPOST Pay wallet."
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <div className="bg-white rounded-xl border border-border p-5">
            <h3 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-nipost-green" /> Your enrolments
            </h3>
            {recipients.isLoading ? (
              <p className="text-sm text-muted inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            ) : recipients.isError ? (
              <p role="alert" className="text-sm text-red-600">
                Could not load disbursements — please try again.
              </p>
            ) : recipients.data && recipients.data.items.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="text-xs text-muted text-left border-b border-border">
                    <tr>
                      <th className="py-2">Programme</th>
                      <th className="py-2">Amount</th>
                      <th className="py-2">Status</th>
                      <th className="py-2">Paid</th>
                      <th className="py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {recipients.data.items.map((r: DisbursementRecipientRow) => (
                      <tr key={r.id} className="border-b border-gray-50 last:border-0">
                        <td className="py-2 font-medium text-gray-900">{r.programName}</td>
                        <td className="py-2 font-medium">{formatNaira(r.amount)}</td>
                        <td className="py-2"><StatusBadge status={r.status} /></td>
                        <td className="py-2 text-muted">
                          {r.paidAt ? new Date(r.paidAt).toLocaleDateString('en-NG') : '—'}
                        </td>
                        <td className="py-2 text-right">
                          {r.status === 'PENDING' ? (
                            <button
                              type="button"
                              onClick={() => claim.mutate(r.id)}
                              disabled={claim.isPending}
                              className="px-3 py-1.5 text-xs font-medium bg-nipost-green text-white rounded-lg hover:bg-nipost-green-dark disabled:opacity-50 inline-flex items-center gap-1"
                              data-testid={`claim-${r.programCode}`}
                            >
                              {claim.isPending && claim.variables === r.id ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Sparkles className="w-3.5 h-3.5" />
                              )}
                              Claim
                            </button>
                          ) : null}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {claim.isSuccess ? (
                  <div
                    className="mt-3 rounded-lg bg-success-light border border-success/20 px-3 py-2 text-xs text-success flex items-center gap-2"
                    role="status"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" /> Claim posted —{' '}
                    {formatNaira(claim.data.amount)} credited. New wallet balance{' '}
                    {formatNaira(claim.data.walletBalance)}.
                  </div>
                ) : null}
                {claim.isError ? (
                  <div role="alert" className="mt-3 rounded-lg bg-red-50 border border-red-200 px-3 py-2 text-xs text-red-700">
                    {claim.error.message}
                  </div>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-muted">
                You're not enrolled in any disbursement programmes yet.
              </p>
            )}
          </div>
        </div>

        <div>
          <div className="bg-white rounded-xl border border-border p-5 lg:sticky lg:top-24">
            <h3 className="font-semibold text-gray-900 mb-3">Active programmes</h3>
            {programs.isLoading ? (
              <p className="text-sm text-muted inline-flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading…
              </p>
            ) : programs.data && programs.data.items.length > 0 ? (
              <ul className="text-sm text-muted space-y-3">
                {programs.data.items.map((p: DisbursementProgram) => (
                  <li key={p.id}>
                    <p className="font-semibold text-gray-900">{p.name}</p>
                    <p className="text-xs">
                      {p.sponsorAgency} · {formatNaira(p.allowanceAmount)} {p.frequency.toLowerCase()}
                    </p>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="text-sm text-muted">No active programmes.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
